import AerChart from '../../../components/vessel/overview/rolling-emissions-calculations/AerChart';
import HistoricalPerformance from '../../../components/vessel/overview/historical-performance/HistoricalPerformance';
import {
	selectCurrentYearVesselCiiAlignmentData,
	selectEnvironmentalCredentialsForCurrentYear,
	selectHistoricalPerformanceData,
} from '../../../greenSelector';
import { useAppDispatch, useAppSelector } from '../../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../../core/store';
import { useEffect, useRef, useState } from 'react';
import { greenActions, selectVesselCiiAlignments, selectVesselKeyMetricsDateBased } from '../../../greenSlice';
import VesselPpClimateAlignmentChart from '../../../components/vessel/overview/projection/VesselPpClimateAlignmentChart';
import { IVesselFormData } from '../../../../../shared/sharedTypes';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import MetricsCard from '../../../../../shared/components/metricsCard/MetricsCard';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';
import ImoRenderer from '../../../../../shared/components/renderers/ImoRenderer';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../../date-range/slices/dateRangeSlice';
import { selectAnalysisDateRangeString } from '../../../../date-range/dataRangeSelector';

interface IOverviewProps {
	data: IVesselFormData;
	isFleet?: boolean;
}

const Overview: React.FC<IOverviewProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const { msiEnvironmentalCredentialsData, rollingAerValuesData } = useAppSelector((state: RootState) => state.greenSlice);

	const historicalPerformanceData = useAppSelector(selectHistoricalPerformanceData);
	const vesselKeyMetricsDateBased = useAppSelector(selectVesselKeyMetricsDateBased);
	const vesselCiiAlignmentsData = useAppSelector(selectVesselCiiAlignments);
	const currentYearVesselCiiAlignmentData = useAppSelector(selectCurrentYearVesselCiiAlignmentData);
	const environmentalCredentialsForCurrentYear = useAppSelector(selectEnvironmentalCredentialsForCurrentYear);
	// Read from the feature date range slice
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	const dateRangeString= useAppSelector(selectAnalysisDateRangeString);

	useEffect(() => {
		if (props.data.imoNumber && analysisDateRange.dateRange) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;

			if (!startYearMonth || !endYearMonth) return;

			dispatch(greenActions.getMsiEnvironmentalCredentialsRequestAction([props.data.imoNumber]));
			dispatch(greenActions.getRollingAerValuesRequestAction({ imoNumber: props.data.imoNumber, startDate, endDate }));
			dispatch(
				greenActions.getVesselCiiAlignmentsRequestAction({
					imoNumber: props.data.imoNumber,
					startYearMonth: startYearMonth,
					endYearMonth: endYearMonth,
				}),
			);
			dispatch(
				greenActions.getVesselKeyMetricsDateBasedRequestAction({
					imoNumbers: [props.data.imoNumber],
					startYearMonth,
					endYearMonth,
				}),
			);
		}
	}, [dispatch, props.data.imoNumber, analysisDateRange]);

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-col gap-2 w-full h-full">
			<div className="w-full flex-col">
				<span className="font-bold text-msi-midblue-color-500">
					{(props.isFleet && (
						<ImoRenderer
							imoNumber={props.data.imoNumber!}
							displayValue={props.data.name}
							className="text-msi-midblue-color-500 font-bold text-sm"
						/>
					)) ||
						props.data.name}{' '}
					|{' '}
				</span>
				<span className="font-bold">Emissions Profile : </span>
				<span>{dateRangeString}</span>
			</div>
			<div className="flex flex-col xl:flex-row gap-4 w-full h-full">
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col basis-1/2 gap-1 border border-msi-darkgrey-color-300 rounded">
						<ExpandableContainer
							title="12m Rolling Emissions Calculations (Current)"
							panelOffsets={panelOffset}
							expandable={false}
						>
							<div className="flex flex-row h-full justify-between items-start w-full p-4">
								<div className="flex flex-row">
									{vesselKeyMetricsDateBased &&
										((vesselKeyMetricsDateBased.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) ||
											(vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData &&
												vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData.length > 0 && (
													<MetricsCard
														metrics={[
															{
																label: 'Total Distance (nm)',
																value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].totalDistance,
															},
															{
																label: 'Fuel Consumed (T)',
																value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].fuelConsumed,
															},
															{
																label: 'CO₂ Emissions (T, TtW)',
																value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].co2Emissions,
															},
															{
																label: 'CO₂ Emissions (T, WtW)',
																value: vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0].co2EmissionsWtW,
															},
														]}
													/>
												)))}
								</div>

								<div className="flex flex-row">
									{vesselCiiAlignmentsData &&
										((vesselCiiAlignmentsData.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
											<MetricsCard
												metrics={[
													{ label: 'AER (TtW)', value: currentYearVesselCiiAlignmentData?.currentAer },
													{ label: 'AER (WtW)', value: currentYearVesselCiiAlignmentData?.currentAerWtw },
													{ label: 'CII Score', value: currentYearVesselCiiAlignmentData?.ciiScore },
													{ label: 'CII Band', value: currentYearVesselCiiAlignmentData?.currentCii },
												]}
											/>
										))}
								</div>

								<div className="flex flex-row">
									{vesselCiiAlignmentsData &&
										((vesselCiiAlignmentsData.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
											<MetricsCard
												metrics={[
													{
														label: `PP Trajectory Value ('Minimum')`,
														value: environmentalCredentialsForCurrentYear?.ppTrajectoryMinimum,
													},
													{
														label: `Alignment (PP 'Minimum')`,
														value: environmentalCredentialsForCurrentYear?.alignmentMin,
														suffix: '%',
													},
													{
														label: `PP Trajectory Value ('Striving')`,
														value: environmentalCredentialsForCurrentYear?.ppTrajectoryStriving,
													},
													{
														label: `Alignment (PP 'Striving')`,
														value: environmentalCredentialsForCurrentYear?.alignmentStriving,
														suffix: '%',
													},
												]}
											/>
										))}
								</div>
							</div>
						</ExpandableContainer>
					</div>

					<div className="flex flex-col basis-1/2 gap-1 border border-msi-darkgrey-color-300 rounded p-1">
						{(msiEnvironmentalCredentialsData?.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
							<ExpandableContainer title="Annual Data Table" panelOffsets={panelOffset}>
								<HistoricalPerformance data={historicalPerformanceData || []} />
							</ExpandableContainer>
						)}
					</div>
				</div>

				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col basis-1/2 gap-1 border border-msi-darkgrey-color-300 rounded">
						{(rollingAerValuesData?.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
							<ExpandableContainer title="12m Rolling CII Score" panelOffsets={panelOffset}>
								<AerChart data={rollingAerValuesData?.rollingAerValuesData} />
							</ExpandableContainer>
						)}
					</div>

					<div className="flex flex-col basis-1/2 gap-1 border border-msi-darkgrey-color-300 rounded">
						{(msiEnvironmentalCredentialsData?.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
							<ExpandableContainer
								panelOffsets={panelOffset}
								title="Annual AERe / cgDISTe (WtW) estimate vs Poseidon Principle Trajectories"
							>
								<VesselPpClimateAlignmentChart
									chartData={msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsData?.vesselLocationChart}
									data={vesselCiiAlignmentsData?.vesselCiiAlignmentData}
									historicalPerformanceData={historicalPerformanceData || null}
								/>
							</ExpandableContainer>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Overview;
